import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link2, Mail, Pencil, Trash2 } from "lucide-react";

import { communicationService } from "../../services/communicationService";
import { studentService, type Parent } from "../../services/studentService";
import { apiErrorMessage } from "../../utils/apiError";
import { MessageComposer } from "../communication/MessageComposer";
import { ReasonPanel } from "../inventory/InventoryParts";
import { DetailsModal } from "../register/DetailsModal";
import { deleteErrorText } from "../register/RecordParts";
import { deleteBlocker, portalState, relationshipLabel } from "./parentsLogic";

type Action = "reset" | "off" | "on" | null;

/**
 * One parent opened from the register: their children, their portal login
 * and what has been done to it, and what can be done: edit, link children,
 * message them, reset a forgotten password, turn the login off or on.
 */
export function ParentDetails({ parent, onEdit, onLink, onClose, onChanged, onCredentials, onDeleted }: {
  parent: Parent;
  onEdit: () => void;
  onLink: () => void;
  onClose: () => void;
  onChanged: (text: string) => void;
  onCredentials: (username: string, password: string) => void;
  onDeleted: (text: string) => void;
}) {
  const [action, setAction] = useState<Action>(null);
  const [messaging, setMessaging] = useState(false);
  const history = useQuery({ queryKey: ["parents", "access-history", parent.id], queryFn: () => studentService.getParentAccessHistory(parent.id) });
  const reset = useMutation({
    mutationFn: (reason: string) => studentService.resetParentPassword(parent.id, reason),
    onSuccess: (result) => { onCredentials(result.username, result.temporary_password); onClose(); },
  });
  const portal = useMutation({
    mutationFn: ({ active, reason }: { active: boolean; reason: string }) => studentService.setParentPortal(parent.id, active, reason),
    onSuccess: (saved) => { setAction(null); onChanged(`${saved.full_name}'s login is now ${saved.portal_active ? "on" : "off"}.`); history.refetch(); },
  });
  const remove = useMutation({
    mutationFn: () => studentService.deleteParent(parent.id),
    onSuccess: () => onDeleted(`${parent.full_name} deleted.`),
  });
  const send = useMutation({
    mutationFn: communicationService.sendMessage,
    onSuccess: () => { setMessaging(false); onChanged(`Message sent to ${parent.full_name}.`); },
  });
  const state = portalState(parent);
  const blocker = deleteBlocker(parent);
  const name = parent.full_name || parent.user.username;
  const context = (kind: string) => ({ key: "students.parent_note" as const, params: { kind, id: parent.id } });

  if (messaging) {
    return (
      <MessageComposer
        recipients={[{ id: parent.user.id, name, role: "parent" }]}
        pending={send.isPending}
        error={send.isError ? apiErrorMessage(send.error, "The message could not be sent.") : undefined}
        onSend={(draft) => send.mutate(draft)}
        onClose={() => setMessaging(false)}
      />
    );
  }

  return (
    <DetailsModal
      size="3xl"
      title={name}
      headline={{ label: relationshipLabel(parent.relationship), value: name, note: `@${parent.user.username}` }}
      status={state}
      facts={[
        ["Phone", parent.user.phone_number || <span className="text-amber-700">None on record</span>],
        ["Emergency contact", parent.emergency_contact || "—"],
        ["Email", parent.user.email || "—"],
        ["Occupation", [parent.occupation, parent.workplace].filter(Boolean).join(", ") || "—"],
        ["Last signed in", parent.last_login ? parent.last_login.slice(0, 16).replace("T", " ") : "Never"],
        ["Added", parent.created_at?.slice(0, 10) || "—"],
      ]}
      sections={[{ title: "Address", body: parent.address }]}
      onClose={onClose}
      footer={
        <>
          {!blocker && (
            <button type="button" disabled={remove.isPending} onClick={() => window.confirm(`Delete ${name} and their portal login?`) && remove.mutate()} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-outline gap-1.5" disabled={parent.portal_active === false} title={parent.portal_active === false ? "Their login is off" : undefined} onClick={() => { send.reset(); setMessaging(true); }}>
            <Mail className="h-4 w-4" /> Message
          </button>
          <button type="button" className="btn btn-outline gap-1.5" onClick={onLink}><Link2 className="h-4 w-4" /> Children</button>
          <button type="button" className="btn btn-primary gap-1.5" onClick={onEdit}><Pencil className="h-4 w-4" /> Edit</button>
        </>
      }
    >
      {remove.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{deleteErrorText(remove.error)}</p>}
      {blocker && <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">{blocker}</p>}

      <section className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Children</h4>
        {parent.children_data?.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
            {parent.children_data.map((child) => (
              <li key={child.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                <span><span className="font-medium text-gray-900">{child.full_name}</span> <span className="text-xs text-gray-500">{child.student_id} · {child.class_name || "no class"}</span></span>
                {child.is_primary && <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-800">Primary contact</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-amber-700">No child is linked, so they see nothing in the family portal.</p>
        )}
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Portal login</h4>
            <p className="text-xs text-gray-500">{parent.portal_active === false ? "Turned off: they cannot sign in." : parent.must_change_password ? "A temporary password is waiting to be changed at their next sign-in." : "Working."}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {parent.portal_active !== false && <button type="button" className="btn btn-outline h-8 text-xs" aria-pressed={action === "reset"} onClick={() => setAction(action === "reset" ? null : "reset")}>Reset password</button>}
            <button type="button" className="btn btn-outline h-8 text-xs" aria-pressed={action === "off" || action === "on"} onClick={() => setAction(action ? null : parent.portal_active === false ? "on" : "off")}>{parent.portal_active === false ? "Turn login on" : "Turn login off"}</button>
          </div>
        </div>
        {action === "reset" && (
          <ReasonPanel title="Reset the password" description="A new temporary password is made and shown to you once; they choose their own when they sign in. Anywhere they are signed in now is signed out." label="Reason" placeholder="e.g. Called the office; had forgotten it" context={context("password_reset")} button="Reset password" busy={reset.isPending} error={reset.error} errorText="The password could not be reset." onSubmit={(reason) => reset.mutate(reason)} />
        )}
        {(action === "off" || action === "on") && (
          <ReasonPanel title={action === "off" ? "Turn the login off" : "Turn the login back on"} description={action === "off" ? "They are signed out everywhere and cannot sign in. Nothing is deleted." : "They can sign in with their existing password."} label="Reason" placeholder="What the decision rests on" context={context(action === "off" ? "deactivate" : "reactivate")} button={action === "off" ? "Turn login off" : "Turn login on"} danger={action === "off"} busy={portal.isPending} error={portal.error} errorText="The login could not be changed." onSubmit={(reason) => portal.mutate({ active: action === "on", reason })} />
        )}
        {history.data && history.data.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">History</p>
            <ol className="mt-1 space-y-1.5 text-sm">
              {history.data.map((change) => (
                <li key={change.id}>
                  <span className="font-medium text-gray-900">{change.action_label}</span>
                  <span className="text-gray-500"> · {change.created_at.slice(0, 10)} · {change.changed_by}</span>
                  <p className="whitespace-pre-wrap break-words text-gray-600">{change.reason}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>
    </DetailsModal>
  );
}
